import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from '@phosphor-icons/react';
import { cx } from '../cx';
import { TONE_SOFT, type Tone } from '../tone';
import { Icon } from '../Icon';

export type TrendDirection = 'up' | 'down' | 'flat';

/** Colour says whether the change is good for the reader, not which way it goes: rising pay is positive, rising competition is attention. */
const ARROW = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };

export type TrendChipProps = {
  direction: TrendDirection;
  /** Whether this change is good for the reader (ui/tone.ts). Neutral (no colour) unless the caller knows. */
  tone?: Tone;
  /** States the change in words ("Hiring up 18% since August"): the arrow alone is not enough. */
  children: ReactNode;
  /** On an employer brand surface: a white pill that reads on any band colour. */
  onBrand?: boolean;
  className?: string;
};

/** Which way something is moving. Only for real computed trends with a stated period. */
export function TrendChip({ direction, tone = 'neutral', children, onBrand, className }: TrendChipProps) {
  return (
    <span
      className={cx(
        'inline-flex h-6 items-center gap-1 rounded-jc-info px-2.5 text-xs font-semibold whitespace-nowrap',
        onBrand ? 'bg-jc-logo-surface text-jc-on-logo-surface' : TONE_SOFT[tone],
        className,
      )}
    >
      <Icon icon={ARROW[direction]} size="sm" />
      {children}
    </span>
  );
}
